/* Copyright (c) 2017 Backpack Health LLC */
import * as _ from 'lodash';

import IconFileTypeImage from '../app/components/icons/IconFileTypeImage';
import IconFileTypeAudio from '../app/components/icons/IconFileTypeAudio';
import IconFileTypeVideo from '../app/components/icons/IconFileTypeVideo';
import IconFileTypeDocument from '../app/components/icons/IconFileTypeDocument';
import IconFileTypeOther from '../app/components/icons/IconFileTypeOther';
import DateUtil from '../utilities/DateUtil';
import { attachmentTypes } from '../models/AttachmentModel';

const MIME_SUB_TYPES = ['excel', 'msword', 'pdf', 'postscript', 'rtf', 'vnd.ms-excel', 'wordperfect', 'x-excel', 'x-msexcel', 'x-pdf', 'x-rt', 'openxmlformats-officedocument'];

class FileModel {

  static toggleShareCardForNewFile(fileData, shareCardId) {
    const newFileData = {...fileData};
    if (newFileData.included_share_cards.includes(shareCardId)) {
      newFileData.included_share_cards.filter(id => id !== shareCardId);
    } else {
      newFileData.included_share_cards.push(shareCardId)
    }
    return newFileData;
  }

  static createFromRawFile(rawFile) {
    const { name = '', lastModified = new Date() } = rawFile;

    // ie11 bug
    let type = rawFile.type;
    if (!type) {
      type = name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream';
    }

    const nameParts = name.match(/(.*)(?=\.\w+$)/);

    return new FileModel({
      name: nameParts ? nameParts[0] : name,
      flagged: false,
      completed: false,
      mime_type: type,
      byte_length: rawFile.size,
      included_share_cards: [],
      observed: {
        date: DateUtil.format(lastModified),
        precision: 'P1D',
      },
    });
  }

  constructor(data = {}) {
    this.data = _.assign({}, {
      flagged: false,
    }, data);

    this.attachments = [];
    this.parentAttachments = [];
    this.categoryData = {};
  }

  identifier() {
    return this.data.id;
  }

  isNote() {
    return false;
  }

  isFile() {
    return true;
  }

  isTooBig() {
    return this.data.byte_length > 100000000;
  }

  getName() {
    return _.get(this, 'data.name', '');
  }

  getMimeType() {
    return _.get(this, 'data.mime_type', '');
  }

  getComment() {
    return this.data.comment || '';
  }

  setComment(text) {
    this.data.comment = text;
  }

  getObservedDate() {
    return _.get(this, 'data.observed', { date: '', precision: '' });
  }

  markCompleted(value) {
    this.data.completed = value;
  }

  fileName() {
    return this.data.name;
  }

  isFlagged() {
    return _.get(this, 'data.flagged', false);
  }

  hasCategory() {
    return _.get(this, 'data.category_id', null) !== null;
  }

  toggleFlag() {
    this.data.flagged = !this.isFlagged();
  }

  icon() {
    const mimeTypes = _.get(this, 'data.mime_type', '').split('/');
    const subType = mimeTypes[1];

    switch (mimeTypes[0]) {
      case 'image':
        return IconFileTypeImage.Medium;
      case 'text':
        return IconFileTypeDocument.Medium;
      case 'video':
        return IconFileTypeVideo.Medium;
      case 'application':
        for (let i = 0; i < MIME_SUB_TYPES.length; i++) {
          if (subType.indexOf(MIME_SUB_TYPES[i]) > -1) {
            return IconFileTypeDocument.Medium;
          }
        }
        return IconFileTypeOther.Medium;
      case 'audio':
        return IconFileTypeAudio.Medium;
      default:
        return IconFileTypeOther.Medium;
    }
  }

  isPreviewAvailable() {
    const mimeTypes = _.get(this, 'data.mime_type', '').split('/');

    switch (mimeTypes[0]) {
      case 'image':
      case 'video':
      case 'audio':
        return true;
      case 'application':
        return this.isPdf();
      default:
        return false;
    }
  }

  getFileTypeCategory() {
    const mimeTypes = _.get(this, 'data.mime_type', '').split('/');
    const subType = mimeTypes[1];

    switch (mimeTypes[0]) {
      case 'image':
        return 'photos';
      case 'text':
        return 'documents';
      case 'video':
        return 'videos';
      case 'application':
        for (let i = 0; i < MIME_SUB_TYPES.length; i++) {
          if (subType.indexOf(MIME_SUB_TYPES[i]) > -1) {
            return 'documents';
          }
        }
        return 'other';
      case 'audio':
        return 'audio';
      default:
        return 'other';
    }
  }

  setName(name) {
    this.data.name = name.slice(0, 100);
  }

  isVideo() {
    return /video/.test(this.getMimeType());
  }

  isImage() {
    return /image/.test(this.getMimeType());
  }

  isDocument() {
    const mimeTypes = _.get(this, 'data.mime_type', '').split('/');
    const subType = mimeTypes[1];

    if (mimeTypes[0] === 'text') {
      return true;
    } else if (mimeTypes[0] === 'application') {
      for (let i = 0; i < MIME_SUB_TYPES.length; i++) {
        if (subType.indexOf(MIME_SUB_TYPES[i]) > -1) {
          return true;
        }
      }
    }

    return false;
  }

  isAudio() {
    return /audio/.test(this.getMimeType());
  }

  isPdf() {
    return /pdf/.test(this.getMimeType());
  }

  isHtml() {
    return /html/.test(this.getMimeType());
  }

  isOther() {
    const mimeTypes = _.get(this, 'data.mime_type', '').split('/');
    const subType = mimeTypes[1];

    switch (mimeTypes[0]) {
      case 'image':
      case 'text':
      case 'video':
        return false;
      case 'application':
        for (let i = 0; i < MIME_SUB_TYPES.length; i++) {
          if (subType.indexOf(MIME_SUB_TYPES[i]) > -1) {
            return false;
          }
        }
        return true;
      case 'audio':
      default:
        return true;
    }
  }

  isAttached() {
    return this.attachments.length > 0;
  }

  formatCategory() {
    return _.get(this, 'categoryData.key', '');
  }

  getCategoryMessageKey() {
    return _.get(this, 'categoryData.key', '');
  }

  fileSizeData() {
    let size = _.get(this, 'data.byte_length', 0);
    let unit = 'b';

    const sizeKiloBytes = size / 1000;
    const sizeMegaBytes = sizeKiloBytes / 1000;
    const sizeGigaBytes = sizeMegaBytes / 1000;
    const sizeTeraBytes = sizeGigaBytes / 1000;

    if (sizeTeraBytes >= 1) {
      size = sizeTeraBytes;
      unit = 'TB';
    } else if (sizeGigaBytes >= 1) {
      size = sizeGigaBytes;
      unit = 'GB';
    } else if (sizeMegaBytes >= 1) {
      size = sizeMegaBytes;
      unit = 'MB';
    } else if (sizeKiloBytes >= 1) {
      size = sizeKiloBytes;
      unit = 'kB';
    }
    size = Number(size).toFixed(2);
    size = size % 1 === 0 ? parseInt(Math.floor(size), 10) : parseFloat(size, 10);

    return {
      size,
      unit,
    };
  }

  getByteSize() {
    return _.get(this, 'data.byte_length', 0);
  }

  formatObservedDate() {
    const dateData = this.getObservedDate();
    return DateUtil.formattedMediumDate(dateData.date, dateData.precision);
  }

  setAttachments(attachmentList) {
    this.attachments = _.isArray(attachmentList) ? attachmentList : [];
  }

  setParentAttachments(attachmentList) {
    this.parentAttachments = _.isArray(attachmentList) ? attachmentList : [];
  }

  setCategoryId(categoryId) {
    this.data.category_id = categoryId;
  }

  setDate(dateData) {
    this.data.observed = _.assign({}, this.data.observed, dateData);
  }

  getCategoryId() {
    return _.get(this, 'data.category_id', null);
  }

  setCategory(categoryData = {}) {
    this.categoryData = categoryData;
    this.data.category_id = categoryData.id;
  }

  attachmentType() {
    return attachmentTypes.file;
  }

  shareCard = {
    add: (id) => {
      this.data.included_share_cards.push(id);
    },
    remove: (id) => {
      this.data.included_share_cards.reject(id);
    },
  }
}

export default FileModel;
